import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { createSupabaseClient, getSupabase } from '@litopia/shared/supabase';
import { OpenPenPalRuntimeProviders } from './RuntimeProviders';
import { OppNotificationProvider } from './contexts/OppNotificationContext';
import App from './App';
import { logError } from './utils/errorHandler';
import { consumeHandoffSession } from './utils/consumeHandoffSession';
import './globals.css';

// Activate the shared OpenPenPal theme without collapsing its product-specific
// letter-paper palette into Litopia's Web theme.
document.documentElement.dataset.theme = 'openpenpal';

// Initialize shared Supabase client for OpenPenPal
createSupabaseClient({
  url: import.meta.env.VITE_SUPABASE_URL,
  anonKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  platform: 'openpenpal',
  appVersion: import.meta.env.VITE_APP_VERSION,
  isNative: false,
});

// Auto-enable OPP for logged-in users (triggers opp_user_stats creation)
async function ensureOppEnabled() {
  try {
    const sb = getSupabase();
    const { data: { user } } = await sb.auth.getUser();
    if (!user) return;

    const { data: profile } = await sb
      .from('profiles')
      .select('opp_enabled')
      .eq('id', user.id)
      .maybeSingle();

    if (profile && !profile.opp_enabled) {
      await sb.from('profiles').update({ opp_enabled: true }).eq('id', user.id);
    }
  } catch (err) {
    logError(err, 'Enable OPP for user');
  }
}

function render() {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <OpenPenPalRuntimeProviders>
        <BrowserRouter>
          <OppNotificationProvider>
            <App />
          </OppNotificationProvider>
        </BrowserRouter>
      </OpenPenPalRuntimeProviders>
    </React.StrictMode>,
  );
}

// A handoff has to settle before the first paint: a visitor arriving from
// Litopia with a code would otherwise briefly see the login screen.
void consumeHandoffSession({
  supabase: getSupabase(),
  baseUrl: import.meta.env.VITE_SUPABASE_URL,
}).then(() => {
  ensureOppEnabled();
  render();
});
